export interface FormattedDateTime {
  date: string;
  time: string;
}

export interface DateFormatOptions {
  locale?: string;
  includeWeekday?: boolean;
  includeYear?: boolean;
  timeFormat?: '12h' | '24h';
}

/**
 * Formats a session start date string into a readable date and time format
 * @param sessionStartDate - ISO date string from the API
 * @param options - Formatting options
 * @returns Object with formatted date and time strings
 */
export const formatSessionDateTime = (
  sessionStartDate: string,
  options: DateFormatOptions = {}
): FormattedDateTime => {
  const {
    locale = 'en-US',
    includeWeekday = true,
    includeYear = true,
    timeFormat = '12h'
  } = options;

  const date = new Date(sessionStartDate);

  // Build date format options
  const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric'
  };

  if (includeWeekday) {
    dateOptions.weekday = 'long';
  }

  if (includeYear) {
    dateOptions.year = 'numeric';
  }

  const formattedDate = date.toLocaleDateString(locale, dateOptions);

  // Build time format options
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit'
  };

  if (timeFormat === '12h') {
    timeOptions.hour12 = true;
  }

  const formattedTime = date.toLocaleTimeString(locale, timeOptions);

  return { date: formattedDate, time: formattedTime };
};

/**
 * Formats a date for display in a shorter format (e.g., "Dec 16, 2023")
 * @param dateString - ISO date string
 * @returns Formatted date string
 */
export const formatShortDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Formats time for display (e.g., "2:00 PM")
 * @param dateString - ISO date string
 * @returns Formatted time string
 */
export const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Formats a session date into "Sunday, 10 June 2023" format (for ended sessions)
 * @param dateString - ISO date string from the API
 * @param locale - Locale for formatting (default 'en-US')
 * @returns Formatted date string e.g. "Wednesday, 13 December 2025"
 */
export const formatSessionDate = (
  dateString: string,
  locale = 'en-US'
): string => {
  const date = new Date(dateString);
  // toLocaleDateString with these options produces e.g. "Wednesday, 3 December 2025"
  // We strip the comma after the weekday to match design: "Wednesday 3 December 2025"
  return date
    .toLocaleDateString(locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    .replace(/^(\p{L}+),\s*/u, '$1 ');
};

/**
 * Formats a session time range into "02:00pm to 03:30pm" format (for upcoming sessions)
 * @param startDateString - ISO start date string from the API
 * @param endDateString - ISO end date string from the API
 * @param locale - Locale for formatting (default 'en-US')
 * @returns Formatted date+time range string e.g. "Saturday 16 December 2025 • 02:00pm to 03:30pm"
 */
export const formatSessionTimeRange = (
  startDateString: string,
  endDateString: string,
  locale = 'en-US'
): string => {
  const startDate = new Date(startDateString);
  const endDate = new Date(endDateString);

  const weekday = startDate.toLocaleDateString(locale, { weekday: 'long' });
  const day = startDate.getDate();
  const month = startDate.toLocaleDateString(locale, { month: 'long' });
  const year = startDate.getFullYear();
  const datePart = `${weekday} ${day} ${month} ${year}`;

  const formatTime12h = (d: Date): string =>
    d
      .toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
      .toLowerCase()
      .replace(' ', '');

  return `${datePart} • ${formatTime12h(startDate)} to ${formatTime12h(endDate)}`;
};

/**
 * Formats duration in minutes with localized text
 * @param minutes - Duration in minutes (number or string)
 * @param translateFn - Optional translation function (key, fallback) => string
 * @returns Formatted duration string with localized unit
 */
export const formatDuration = (
  minutes: number | string | undefined,
  translateFn?: (key: string, fallback: string) => string
): string => {
  if (typeof minutes === 'number' && minutes > 0) {
    if (translateFn) {
      return `${minutes} ${translateFn("minutes", "Minutes")}`;
    }
    return `${minutes} Minutes`;
  }

  // Fallback for missing or invalid duration
  if (translateFn) {
    return translateFn("duration_not_specified", "Duration not specified");
  }
  return "Duration not specified";
};
